import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import config from "../../config/config";
import { ThemeContext } from "../../context/ThemeContext";
import Logo from "../../components/logo/Logo";
import { useCompanyAuthPath } from "../../hooks/useCompanyAuthPath";
import Toast from "../../components/common/Toast";
import FloatingInput from "../../components/input/FloatingInput";
import CustomSelect from "../../components/customselect/CustomSelect";
import { TooltipProvider } from "../../components/ui/Tooltip";
import { StatesSelectorMulti, CountiesPanel } from "../../components/admin/SalesRepFormParts";
import useCountyCache from "../../hooks/useCountyCache";
import buildSelectedLocations from "../../utils/buildSelectedLocations";
import parseOptionValue from "../../utils/parseOptionValue";
import { Briefcase, MapPinned, ShieldCheck, Sparkles } from "lucide-react";
import "./SalesRepRegister.css";

/** Subtle dot grid for hero depth (white on dark). */
const HERO_DOT_GRID =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'%3E%3Ccircle cx='2' cy='2' r='1' fill='%23fff' fill-opacity='0.11'/%3E%3C/svg%3E\")";

/**
 * Public self-registration for sales reps. Request body matches admin create
 * (POST /admin/sales-reps/:maincompanyid). The API must expose an unauthenticated
 * route that accepts the same payload — default: POST /auth/sales-rep-register
 */
const SALES_REP_PUBLIC_REGISTER_PATH = "/auth/sales-rep-register";

const initialForm = {
  firstname: "",
  lastname: "",
  email: "",
  phone: "",
  title: "",
  username: "",
  password: "",
  confirmPassword: "",
  address1: "",
  address2: "",
  address_city: "",
  address_state: "",
  address_county: "",
  address_zip: "",
};

export default function SalesRepRegister() {
  const { t: trans } = useTranslation();
  const theme = useContext(ThemeContext);
  const mainCompanyId = theme?.id;

  const [states, setStates] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [modalStateIds, setModalStateIds] = useState([]);
  const [modalCountiesMap, setModalCountiesMap] = useState({});
  const [modalSelectedCounties, setModalSelectedCounties] = useState({});

  const navigate = useNavigate();
  const { companyPath } = useCompanyAuthPath();
  const [toast, setToast] = useState({
    visible: false,
    message: "",
    type: "success",
  });
  const emailValidRef = useRef(null);
  const usernameValidRef = useRef(null);

  const { cache: countyCache, fetchCountiesForState, setCache } =
    useCountyCache(mainCompanyId);

  const showToast = useCallback((message, type = "success", duration = 4000) => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), duration);
  }, []);

  useEffect(() => {
    setModalCountiesMap((m) => ({ ...countyCache, ...m }));
  }, [countyCache]);

  useEffect(() => {
    if (!mainCompanyId) return;
    let cancelled = false;
    api
      .get(`/auth/states`, { params: { maincompanyid: mainCompanyId } })
      .then((res) => {
        const fetched = res.data?.data || [];
        if (!cancelled) setStates(fetched);
      })
      .catch(() => {
        if (!cancelled) setStates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [mainCompanyId]);

  const handleCreateStatesChange = useCallback(
    async (e) => {
      const opts = Array.from(e.target.selectedOptions).map((o) =>
        parseOptionValue(o.value)
      );
      setModalStateIds(opts);
      await Promise.all(opts.map((sid) => fetchCountiesForState(sid)));
      setModalSelectedCounties((m) => {
        const copy = { ...m };
        opts.forEach((sid) => {
          if (!copy[sid]) copy[sid] = [];
        });
        Object.keys(copy).forEach((k) => {
          if (!opts.includes(parseOptionValue(k))) delete copy[k];
        });
        return copy;
      });
    },
    [fetchCountiesForState]
  );

  const handleAddressFieldChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }, []);

  const handleZipChange = useCallback((e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 5);
    setForm((f) => ({ ...f, address_zip: digitsOnly }));
  }, []);

  const handleAddressStateSelect = useCallback(
    async (val) => {
      const v = parseOptionValue(val);
      setForm((f) => ({ ...f, address_state: v, address_county: "" }));
      if (v) await fetchCountiesForState(v);
    },
    [fetchCountiesForState]
  );

  const handleAddressCountySelect = useCallback((val) => {
    setForm((f) => ({ ...f, address_county: parseOptionValue(val) }));
  }, []);

  const handleToggleCounty = useCallback((sid, cid, checked) => {
    setModalSelectedCounties((m) => {
      const cur = m[sid] ? [...m[sid]] : [];
      if (checked) {
        if (!cur.includes(cid)) cur.push(cid);
      } else {
        const idx = cur.indexOf(cid);
        if (idx >= 0) cur.splice(idx, 1);
      }
      return { ...m, [sid]: cur };
    });
  }, []);

  const handleRemoveCountyTag = useCallback((sid, cid) => {
    setModalSelectedCounties((m) => ({
      ...m,
      [sid]: (m[sid] || []).filter((x) => x !== cid),
    }));
  }, []);

  const handleEmailBlur = useCallback(
    async (e) => {
      const email = typeof e === "string" ? e : e?.target?.value;
      if (!email || !mainCompanyId) return;
      try {
        const res = await api.get("/auth/check-email", {
          params: {
            email,
            maincompanyid: mainCompanyId,
            user_type: "S",
          },
        });
        if (res.data?.status !== "success") {
          emailValidRef.current = false;
          setErrors((prev) => ({ ...prev, email: "Email already exists." }));
        } else {
          emailValidRef.current = true;
          setErrors((prev) => {
            const next = { ...prev };
            delete next.email;
            return next;
          });
        }
      } catch {
        emailValidRef.current = false;
        setErrors((prev) => ({
          ...prev,
          email: "Email validation failed. Please try again.",
        }));
      }
    },
    [mainCompanyId]
  );

  const handleEmailChange = useCallback(() => {
    emailValidRef.current = null;
    setErrors((prev) => {
      const next = { ...prev };
      delete next.email;
      return next;
    });
  }, []);

  const handleUsernameBlur = useCallback(
    async (eOrValue) => {
      const username =
        typeof eOrValue === "string" ? eOrValue : eOrValue?.target?.value;
      if (!username || !mainCompanyId) return;
      try {
        const res = await api.get("/auth/check-username", {
          params: { username, maincompanyid: mainCompanyId },
        });
        if (res.data?.status !== "success") {
          usernameValidRef.current = false;
          setErrors((prev) => ({
            ...prev,
            username: trans("register_username_exists"),
          }));
        } else {
          usernameValidRef.current = true;
          setErrors((prev) => {
            const next = { ...prev };
            delete next.username;
            return next;
          });
        }
      } catch {
        usernameValidRef.current = false;
        setErrors((prev) => ({
          ...prev,
          username: trans("register_username_validation_failed"),
        }));
      }
    },
    [mainCompanyId, trans]
  );

  const handleUsernameChange = useCallback(() => {
    usernameValidRef.current = null;
    setErrors((prev) => {
      const next = { ...prev };
      delete next.username;
      return next;
    });
  }, []);

  /** Same formatting as Register.js */
  const formatPhoneNumber = (input) => {
    const digits = String(input || "").replace(/[^0-9]/g, "").slice(0, 12);
    if (digits.length === 0) return "";
    let formatted = "";
    const length = digits.length;
    if (length <= 10) {
      for (let i = 0; i < length; i++) {
        if (i === 3 || i === 6) formatted += "-";
        formatted += digits[i];
      }
    } else if (length === 11) {
      formatted = `${digits[0]} ${digits.substring(1, 4)}-${digits.substring(4, 7)}-${digits.substring(7, 11)}`;
    } else if (length === 12) {
      formatted = `${digits.substring(0, 2)} ${digits.substring(2, 5)}-${digits.substring(5, 8)}-${digits.substring(8, 12)}`;
    }
    return formatted;
  };

  const normalizePhoneNumber = (formattedPhone) => {
    const digits = String(formattedPhone || "").replace(/[^0-9]/g, "");
    if (digits.length === 10) {
      return `${digits.substring(0, 3)}-${digits.substring(3, 6)}-${digits.substring(6, 10)}`;
    }
    if (digits.length === 11) {
      return `${digits.substring(0, 1)} ${digits.substring(1, 4)}-${digits.substring(4, 7)}-${digits.substring(7, 11)}`;
    }
    if (digits.length === 12) {
      return `${digits.substring(0, 2)} ${digits.substring(2, 5)}-${digits.substring(5, 8)}-${digits.substring(8, 12)}`;
    }
    return digits;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, phone: formatPhoneNumber(value) }));
  };

  const handlePhoneKeyDown = (e) => {
    if ([8, 9, 46, 37, 38, 39, 40, 36, 35].includes(e.keyCode)) return;
    if (e.keyCode >= 48 && e.keyCode <= 57) return;
    if (e.keyCode >= 96 && e.keyCode <= 105) return;
    if ((e.ctrlKey || e.metaKey) && [65, 67, 86, 88].includes(e.keyCode)) return;
    e.preventDefault();
  };

  const validateForm = () => {
    const e = {};
    const first = (form.firstname || "").trim();
    const last = (form.lastname || "").trim();
    if (!first && !last) e.name = "First or last name is required.";

    const email = (form.email || "").trim();
    if (!email) e.email = "Email is required.";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email))
      e.email = "Invalid email.";
    else if (emailValidRef.current === false) e.email = "Email already exists.";

    if (!((form.title || "").trim())) e.title = "Title is required.";

    const username = (form.username || "").trim();
    if (!username) e.username = trans("validation_username_required");
    else if (username.length < 6) e.username = trans("validation_username_min");
    else if (usernameValidRef.current === false)
      e.username = trans("register_username_exists");

    const pwd = form.password || "";
    if (!pwd) e.password = trans("validation_password_required");
    else if (pwd.length < 6) e.password = trans("validation_password_min");
    else if (!/[a-z]/.test(pwd))
      e.password = trans("validation_password_lowercase");
    else if (!/\d/.test(pwd)) e.password = trans("validation_password_number");
    else if (!/[=!\-@._&%$#?'"<>^*]/.test(pwd))
      e.password = trans("validation_password_special");

    const confirm = form.confirmPassword || "";
    if (!confirm) e.confirmPassword = trans("validation_confirm_password_required");
    else if (confirm !== pwd) e.confirmPassword = trans("validation_password_mismatch");

    if (!modalStateIds || modalStateIds.length === 0) {
      e.state = "Select at least one state for service areas.";
    }

    const totalSelectedCounties = Object.values(modalSelectedCounties || {}).reduce(
      (acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0),
      0
    );
    if (totalSelectedCounties === 0) {
      e.county = "Select at least one county for your service areas.";
    }

    if (!(form.address1 || "").trim()) e.address1 = "Address 1 is required.";
    if (!(form.address_city || "").trim()) e.address_city = "City is required.";
    if (!form.address_state) e.address_state = "State is required.";
    if (!form.address_county) e.address_county = "County is required.";
    const zip = (form.address_zip || "").trim();
    if (!zip) e.address_zip = "ZIP code is required.";
    else if (!/^\d{5}$/.test(zip))
      e.address_zip = "Enter a valid 5-digit ZIP code.";

    setErrors(e);
    return Object.keys(e).length ? e : null;
  };

  const resetForm = () => {
    setForm(initialForm);
    setModalStateIds([]);
    setModalSelectedCounties({});
    setFormError("");
    setErrors({});
    emailValidRef.current = null;
    usernameValidRef.current = null;
    if (typeof setCache === "function") setCache({});
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!mainCompanyId) {
      setFormError("Company context is not loaded yet. Please refresh.");
      return;
    }
    if (emailValidRef.current === false || usernameValidRef.current === false) {
      if (emailValidRef.current === false) {
        setErrors((prev) => ({
          ...prev,
          email: trans("register_email_exists"),
        }));
      }
      if (usernameValidRef.current === false) {
        setErrors((prev) => ({
          ...prev,
          username: trans("register_username_exists"),
        }));
      }
      setFormError("Please fix the errors below.");
      return;
    }

    const v = validateForm();
    if (v) {
      setFormError("Please fix the errors below.");
      return;
    }
    setFormError("");

    const { selected_locations, primaryStateId, primaryCountyId } =
      buildSelectedLocations({
        modalStateIds,
        modalSelectedCounties,
        modalCountiesMap,
        states,
      });

    const payload = {
      firstname: form.firstname || "",
      lastname: form.lastname || "",
      email: (form.email || "").trim(),
      phone: normalizePhoneNumber(form.phone),
      title: (form.title || "").trim(),
      maincompanyid: mainCompanyId,
      stateid: primaryStateId || null,
      countyid: primaryCountyId || null,
      address1: (form.address1 || "").trim(),
      address2: (form.address2 || "").trim(),
      address_city: (form.address_city || "").trim(),
      address_state: form.address_state || null,
      address_county: form.address_county || null,
      address_zip: (form.address_zip || "").trim(),
      selected_locations,
      username: (form.username || "").trim(),
      password: form.password || "",
    };

    try {
      setSaving(true);
      const res = await api.post(SALES_REP_PUBLIC_REGISTER_PATH + "/" + mainCompanyId, payload, {
        skipAuthRedirect: true,
      });
      const ok =
        res.status === 201 ||
        res.status === 200 ||
        res.data?.status === "success";
      if (ok) {
        showToast("Thank you. Your registration has been submitted.", "success");
        resetForm();
        // redirect to login page with set timeout
        setTimeout(() => {
          navigate(companyPath(""));
        }, 2000);
      } else {
        const msg = "Registration could not be completed.";
        setFormError(msg);
        showToast(msg, "error");
      }
    } catch (err) {
      console.error("Sales rep registration failed", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Registration failed. Please try again or contact support.";
      setFormError(msg);
      showToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const heroImageUrl = useMemo(() => {
    if (!theme?.background_image) return null;
    const base = config.assetsBaseUrl.replace(/\/$/, "");
    const path = String(theme.background_image).replace(/^\/+/, "");
    return `${base}/${path}`;
  }, [theme?.background_image]);

  const companyLine =
    theme?.main_company_name || theme?.name || config.appName;

  const heroHighlights = useMemo(
    () => [
      {
        Icon: MapPinned,
        title: "Your markets",
        text: "Select states and counties you represent—clear coverage from day one.",
      },
      {
        Icon: ShieldCheck,
        title: "Your credentials",
        text: "Pick a username and password for a secure, personal sign-in.",
      },
      {
        Icon: Briefcase,
        title: "Your brand",
        text: "This page follows your company theme and colors automatically.",
      },
    ],
    []
  );

  return (
    <TooltipProvider>
      <div className="srp-register">
        {/* Left column — layered hero: image, gradients, glass panel, highlights */}
        <div className="srp-left">
          {/* Photo / fallback base */}
          <div
            className="srp-bg-photo"
            style={{
              backgroundImage: heroImageUrl ? `url(${heroImageUrl})` : undefined,
              transform: "scale(1.04)",
            }}
            aria-hidden
          />
          {/* Brand-tinted veil + depth */}
          <div
            className="srp-abs-fill"
            style={{
              background: `linear-gradient(145deg, 
                rgba(15, 23, 42, 0.88) 0%, 
                rgba(15, 23, 42, 0.55) 42%, 
                rgba(30, 41, 59, 0.78) 100%)`,
            }}
            aria-hidden
          />
          <div className="srp-bg-vignette" aria-hidden />
          <div className="srp-glow-sky" aria-hidden />
          <div className="srp-glow-white" aria-hidden />
          {/* Fine grid texture */}
          <div
            className="srp-dot-layer"
            style={{ backgroundImage: HERO_DOT_GRID }}
            aria-hidden
          />

          <div className="srp-hero">
            <div className="srp-hero-main">
              <div className="srp-hero-pill">
                <Sparkles className="srp-lucide srp-lucide--sm" strokeWidth={2} aria-hidden />
                <span className="srp-hero-pill-text">Sales rep program</span>
              </div>

              <div className="srp-glass-panel">
                <div className="srp-logo-row">
                  <div className="srp-logo-inner">
                    <Logo type="primary" size={168} />
                  </div>
                </div>
                <p className="srp-hero-eyebrow">{companyLine}</p>
                <h1 className="srp-hero-title">Sales representative registration</h1>
                <p className="srp-hero-lede srp-hero-muted">
                  Join {companyLine} as a sales rep. Complete the form to submit
                  your profile, territories, and sign-in details.
                </p>
              </div>

              <ul className="srp-highlights">
                {heroHighlights.map(({ Icon, title, text }) => (
                  <li key={title} className="srp-highlight">
                    <div className="srp-highlight-ico" aria-hidden>
                      <Icon className="srp-lucide srp-lucide--lg" strokeWidth={1.75} />
                    </div>
                    <div>
                      <p className="srp-highlight-title">{title}</p>
                      <p className="srp-highlight-desc">{text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <footer className="srp-footer">
              <div className="srp-footer-brand">
                <Logo type="secondaryWhite" size={140} />
              </div>
              <p className="srp-footer-copy">
                © {new Date().getFullYear()} {companyLine}. All rights reserved.
              </p>
            </footer>
          </div>
        </div>

        <div className="srp-right">
          {/* Mobile hero — compact glass strip */}
          <div className="srp-mobile-strip">
            <div
              className="srp-bg-photo"
              style={{
                backgroundImage: heroImageUrl ? `url(${heroImageUrl})` : undefined,
              }}
              aria-hidden
            />
            <div
              className="srp-abs-fill"
              style={{
                background: `linear-gradient(160deg, rgba(15,23,42,0.9) 0%, rgba(30,41,59,0.82) 100%)`,
              }}
              aria-hidden
            />
            <div className="srp-hero srp-mobile-inner">
              <div className="srp-hero-pill srp-hero-pill--compact">
                <Sparkles className="srp-lucide srp-lucide--xs" strokeWidth={2} aria-hidden />
                <span className="srp-hero-pill-text srp-hero-pill-text--sm">Sales rep</span>
              </div>
              <div className="srp-mobile-card">
                <div className="srp-mobile-card-inner">
                  <Logo type="primary" size={132} />
                </div>
                <h2 className="srp-mobile-h2">{companyLine}</h2>
                <p className="srp-mobile-tagline srp-hero-muted">
                  Sales representative registration
                </p>
              </div>
            </div>
          </div>

          <div className="srp-form-scroll">
            <div className="srp-panel">
              <div>
                <h2 className="srp-h2">Create sales rep profile</h2>
                <p className="srp-intro">
                  {trans("register_already")}{" "}
                  <Link to={companyPath("")} className="srp-a">
                    {trans("register_login_link")}
                  </Link>
                </p>
              </div>

              <form onSubmit={handleSubmit} className="srp-form">

                {formError && (
                  <p className="srp-field-error" role="alert">
                    {formError}
                  </p>
                )}

                <div className="srp-section">
                  <div className="srp-row2">
                    <div className="srp-form-group">
                      <FloatingInput
                        label={trans("register_first_name")}
                        name="firstname"
                        value={form.firstname}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, firstname: e.target.value }))
                        }
                      />
                      {errors.name && (
                        <p className="srp-field-error">{errors.name}</p>
                      )}
                    </div>
                    <div className="srp-form-group">
                      <FloatingInput
                        label={trans("register_last_name")}
                        name="lastname"
                        value={form.lastname}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, lastname: e.target.value }))
                        }
                      />
                      {errors.name && (
                        <p className="srp-field-error">{errors.name}</p>
                      )}
                    </div>
                  </div>
                  <div className="srp-row2">
                    <div className="srp-form-group">
                      <FloatingInput
                        label={trans("register_email")}
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={(e) => {
                          setForm((f) => ({ ...f, email: e.target.value }));
                          handleEmailChange();
                        }}
                        onBlur={handleEmailBlur}
                      />
                      {errors.email && (
                        <p className="srp-field-error">{errors.email}</p>
                      )}
                    </div>
                    <div className="srp-form-group">
                      <FloatingInput
                        label={trans("register_phone")}
                        name="phone"
                        value={form.phone}
                        onChange={handlePhoneChange}
                        onKeyDown={handlePhoneKeyDown}
                        placeholder="XXX-XXX-XXXX"
                        maxLength={17}
                        autoComplete="tel"
                      />
                      {errors.phone && (
                        <p className="srp-field-error">{errors.phone}</p>
                      )}
                    </div>
                  </div>
                  <div className="srp-row2">
                    <div className="srp-form-group input-sticky">
                      <FloatingInput
                        label="Title"
                        name="title"
                        placeholder="Sales Manager"
                        className="inpt-pla"
                        value={form.title}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, title: e.target.value }))
                        }
                      />
                      {errors.title && (
                        <p className="srp-field-error">{errors.title}</p>
                      )}
                    </div>

                    <div className="srp-form-group">
                      <FloatingInput
                        label={trans("register_username")}
                        name="username"
                        value={form.username}
                        autoComplete="username"
                        onChange={(e) => {
                          setForm((f) => ({ ...f, username: e.target.value }));
                          handleUsernameChange();
                        }}
                        onBlur={handleUsernameBlur}
                      />
                      {errors.username && (
                        <p className="srp-field-error">{errors.username}</p>
                      )}
                    </div>
                  </div>
                  <div className="srp-row2">
                    <div className="srp-form-group">
                      <FloatingInput
                        type="password"
                        label={trans("register_password")}
                        name="password"
                        value={form.password}
                        autoComplete="new-password"
                        onChange={(e) =>
                          setForm((f) => ({ ...f, password: e.target.value }))
                        }
                      />
                      {errors.password ? (
                        <p className="srp-field-error">{errors.password}</p>
                      ) : (
                        <p className="srp-pwd-hint">
                          At least 6 characters, with a lowercase letter, a number, and a special character (e.g. ! @ # $ %).
                        </p>
                      )}
                    </div>
                    <div className="srp-form-group">
                      <FloatingInput
                        type="password"
                        label={trans("register_confirm_password")}
                        name="confirmPassword"
                        value={form.confirmPassword}
                        autoComplete="new-password"
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            confirmPassword: e.target.value,
                          }))
                        }
                      />
                      {errors.confirmPassword && (
                        <p className="srp-field-error">{errors.confirmPassword}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="srp-section">
                  <div className="srp-form-group">
                    <div className="srp-floating-select-wrap">
                      <StatesSelectorMulti
                        states={states}
                        modalStateIds={modalStateIds}
                        onChange={handleCreateStatesChange}
                      />
                      <label className="srp-floating-select-label">State</label>
                    </div>
                    {errors.state && (
                      <p className="srp-state-error">{errors.state}</p>
                    )}
                  </div>
                  <div className="srp-form-group">
                    <div className="srp-floating-select-wrap">
                      {!modalStateIds.length ? (
                        <p className="srp-hintbox">
                          Select one or more states above, then choose counties
                          (e.g. Los Angeles, California).
                        </p>
                      ) : (
                        <CountiesPanel
                          modalStateIds={modalStateIds}
                          states={states}
                          modalCountiesMap={modalCountiesMap}
                          modalSelectedCounties={modalSelectedCounties}
                          modalMissing={{}}
                          onToggleCounty={handleToggleCounty}
                          onRemoveCountyTag={handleRemoveCountyTag}
                        />
                      )}
                      <label className="srp-floating-select-label">County</label>
                    </div>
                    {errors.county && (
                      <p className="srp-county-error">{errors.county}</p>
                    )}
                  </div>

                  <div className="srp-row2">
                    <div className="srp-form-group">
                      <FloatingInput
                        label={`${trans("register_address")} 1`}
                        name="address1"
                        value={form.address1}
                        onChange={handleAddressFieldChange}
                        autoComplete="street-address"
                      />
                      {errors.address1 && (
                        <p className="srp-field-error">{errors.address1}</p>
                      )}
                    </div>
                    <div className="srp-form-group">
                      <FloatingInput
                        label={`${trans("register_address")} 2`}
                        name="address2"
                        value={form.address2}
                        onChange={handleAddressFieldChange}
                      />
                    </div>
                  </div>
                  <div className="srp-row2">
                    <div className="srp-form-group">
                      <FloatingInput
                        label={trans("register_city")}
                        name="address_city"
                        value={form.address_city}
                        onChange={handleAddressFieldChange}
                        autoComplete="address-level2"
                      />
                      {errors.address_city && (
                        <p className="srp-field-error">{errors.address_city}</p>
                      )}
                    </div>
                    <div className="srp-form-group">
                      <FloatingInput
                        label={trans("register_zip")}
                        name="address_zip"
                        value={form.address_zip}
                        onChange={handleZipChange}
                        maxLength={5}
                        inputMode="numeric"
                        autoComplete="postal-code"
                      />
                      {errors.address_zip && (
                        <p className="srp-field-error">{errors.address_zip}</p>
                      )}
                    </div>
                  </div>
                  <div className="srp-row2">
                    <div className="srp-form-group">
                      <CustomSelect
                        name="address_state"
                        options={states}
                        placeholder={trans("register_state")}
                        label="State"
                        value={form.address_state}
                        onChange={handleAddressStateSelect}
                      />
                      {errors.address_state && (
                        <p className="srp-field-error">{errors.address_state}</p>
                      )}
                    </div>
                    <div className="srp-form-group">
                      <CustomSelect
                        name="address_county"
                        options={modalCountiesMap[form.address_state] || []}
                        placeholder={trans("register_county")}
                        label="County"
                        value={form.address_county}
                        onChange={handleAddressCountySelect}
                        disabled={!form.address_state}
                      />
                      {errors.address_county && (
                        <p className="srp-field-error">{errors.address_county}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="srp-btn-wrap">
                  <button
                    type="submit"
                    disabled={saving || !mainCompanyId}
                    className="submit-btn btn-bg inpt-btn"
                  >
                    {saving ? "Submitting…" : "Register sales rep"}
                  </button>
                </div>
              </form>

              <p className="srp-intro srp-footer-note">
                {trans("register_footer_help")}{" "}<br />
                <Link to={companyPath("contact")} rel="noopener noreferrer" className="srp-a">
                  {trans("register_footer_link")}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <Toast
        visible={toast.visible}
        message={toast.message}
        type={toast.type}
      />
    </TooltipProvider>
  );
}
